import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Search, Mail, Phone, Trash2, CheckCircle, Clock, CheckCircle2 } from 'lucide-react';
import './MasterAdmin.css';

const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContacts();
  }, [filter]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const query = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api(`/master-admin/contacts${query}`);
      const data = await response.json();
      if (response.ok) setContacts(data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await api(`/master-admin/contacts/${id}/status`, {
        method: 'PUT',
        body: { status: newStatus }
      });
      if (response.ok) {
        setContacts(contacts.map(c => c._id === id ? { ...c, status: newStatus } : c));
      }
    } catch (err) {
      console.error('Error updating contact status:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact request?')) return;
    try {
      const response = await api(`/master-admin/contacts/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setContacts(contacts.filter(c => c._id !== id));
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  const filteredContacts = contacts.filter(c => 
    (c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
    (c.email?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
    (c.phone?.includes(searchTerm) || '')
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new': return <span className="ma-badge" style={{ background: 'rgba(59,130,246,0.2)', color: '#60a5fa' }}>New</span>;
      case 'read': return <span className="ma-badge" style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>Read</span>;
      case 'replied': return <span className="ma-badge" style={{ background: 'rgba(16,185,129,0.2)', color: '#34d399' }}>Replied</span>;
      default: return null;
    }
  };

  return (
    <div>
      <div className="ma-header">
        <h1>Contact Inbox</h1>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, position: 'relative', minWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--ma-text-muted)' }} />
          <input 
            type="text" 
            placeholder="Search by name, email or phone..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '0.5rem', background: 'var(--ma-bg-card)', border: '1px solid var(--ma-border)', color: 'white', outline: 'none' }}
          />
        </div>
        <select 
          value={filter} 
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', background: 'var(--ma-bg-card)', border: '1px solid var(--ma-border)', color: 'white', outline: 'none', cursor: 'pointer' }}
        >
          <option value="all">All Statuses</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>
      </div>

      <div className="ma-table-container">
        <table className="ma-table">
          <thead>
            <tr>
              <th>Contact Info</th>
              <th>Subject & Message</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>Loading contacts...</td></tr>
            ) : filteredContacts.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>No contact requests found.</td></tr>
            ) : (
              filteredContacts.map(contact => (
                <tr key={contact._id}>
                  <td>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>{contact.name || 'N/A'}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--ma-text-muted)', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}><Mail size={12}/> {contact.email}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--ma-text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}><Phone size={12}/> {contact.phone}</div>
                  </td>
                  <td style={{ maxWidth: '300px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>{contact.subject || 'No Subject'}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--ma-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {contact.message}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--ma-text-muted)' }}>
                    {new Date(contact.createdAt).toLocaleDateString()}
                  </td>
                  <td>{getStatusBadge(contact.status)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <select 
                        value={contact.status}
                        onChange={(e) => handleStatusChange(contact._id, e.target.value)}
                        style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', background: 'var(--ma-bg-dark)', border: '1px solid var(--ma-border)', color: 'white', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        <option value="new">Mark New</option>
                        <option value="read">Mark Read</option>
                        <option value="replied">Mark Replied</option>
                      </select>
                      <button onClick={() => handleDelete(contact._id)} className="ma-btn-delete" style={{ padding: '0.25rem 0.5rem' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactsManagement;
